import React, { useEffect, useState } from "react";
import styles from "./referral.module.css";
import { deleteData, getData, postData, SearchOptions } from "utils/queries";
import FormFields from "components/Form";
import ContainerHeader from "common/ContainerHeader";
import Button from "common/Button";
import DataTable_ from "common/DataTable";
import Badge from "common/Badge";
import PopUpModal from "common/Popup";
import { allowOnlyAlphaAndSpace } from "utils";
import toast from "react-hot-toast";
import SelectSearch from "common/SelectSearch";

const defaultValues = {
  discount_type:"referral"
}
const Referral = () => {
  const [referrals, setReferrals] = useState(defaultValues);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);
  const [eventFilter, setEventFilter] = useState(null);

  const fetchOptions = async (search = "") => {
    try {
      let optionsResult;
      
      if (!search || search.trim() === "") {
        // Load all upcoming events when no search term
        optionsResult = await SearchOptions(`event/events?eventType=upcoming&limit=100&offset=0&currentDate=${new Date().toISOString()}`);
        return optionsResult?.data?.data?.rows?.map((e) => ({
          value: e?.id,
          label: e?.event_name,
        })) || [];
      } else {
        // Use search endpoint when user types
        optionsResult = await SearchOptions(`ticket/search?key=${search}`);
        return optionsResult?.data?.data
          ?.filter((e) =>
            e?.event_name?.toLowerCase().includes(search.toLowerCase())
          )
          ?.map((e) => ({ value: e?.id, label: e?.event_name })) || [];
      }
    } catch (error) {
      console.error("Error fetching options:", error);
      return [];
    }
  };

  // Validate code format: alphanumeric, no spaces, min 3 chars, max 20 chars
  const isValidCode = (code) => {
    const codeRegex = /^[A-Za-z0-9]{3,20}$/;
    return codeRegex.test(code);
  };

  const handleChange = (label) => (e) => {
    if (label === "name") {
      e.target.value = allowOnlyAlphaAndSpace(e);
    }
    // Convert code to uppercase and remove spaces
    if (label === "code") {
      e.target.value = e.target.value.toUpperCase().replace(/\s/g, "");
    }
    setReferrals((prevDetails) => ({
      ...prevDetails,
      [label]: e?.target ? e?.target?.value : e,
    }));
  };

  const formFields = [
    {
      label: "Name",
      id: "name",
      placeholder: "Name",
      type: "text",
      value: referrals?.name || "",
      default: referrals?.name,
      required: true,
      onChange: handleChange("name"),
    },
    {
      label: "Email",
      id: "email",
      placeholder: "Email",
      type: "email",
      value: referrals?.email || "",
      required: true,
      onChange: handleChange("email"),
    },
    {
      label: "Event Name",
      id: "name",
      placeholder: "Event Name",
      loadOptions: fetchOptions,
      isSearchable: true,
      type: "select_search",
      value: referrals?.event_name || "",
      default: referrals?.event_name,
      required: true,
      loading: true,
      onChange: handleChange("event_name"),
    },
    // {
    //   label: "Discount Type",
    //   id: "discount_type",
    //   placeholder: "Discount Type",
    //   type: "radio",
    //   radioClasses: {
    //     display: "inline-flex",
    //     alignItems: "center",
    //   },
    //   selectedValue: referrals?.discount_type,

    //   options: [
    //     { label: "Referral", value: "referral" },
    //     { label: "Individual", value: "individual" },
    //   ],
    //   onChange: handleChange("discount_type"),
    // },
    {
      label: "Code",
      id: "code",
      placeholder: "e.g. WELCOME25, BLACKFRIDAY25",
      type: "text",
      value: referrals?.code || "",
      required: true,
      onChange: handleChange("code"),
      maxLength: 20,
      minLength: 3,
    },
    {
      label: "Discount(%)",
      id: "discount",
      placeholder: "10",
      type: "number",
      min: 0,
      max: 100,
      value: referrals?.discount || "",
      required: true,
      onChange: handleChange("discount"),
    },
    {
      label: "Commission(%)",
      id: "commission",
      placeholder: "10",
      type: "number",
      min: 0,
      max: 100,
      value: referrals?.commission || "",
      required: true,
      onChange: handleChange("commission"),
    },
  ];

  const deleteSale = async (row) => {
    try {
      const result = await deleteData(
        `referrals/events-referrals/ref/${row?.id}/${!row?.is_active}`, false
      );
      if (result) {
        toast.success(`Referral ${row?.is_active ? 'deactivated' : 'activated'} successfully`);
        fetchReferral();
      }
    } catch (error) {
      toast.error("Failed to update referral status");
    }
  };

  const openDeleteModal = (row) => {
    setRowToDelete(row);
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setRowToDelete(null);
  };

  const permanentDelete = async () => {
    if (!rowToDelete) return;
    try {
      const result = await deleteData(`referrals/events-referrals/permanent/${rowToDelete?.id}`, false);
      if (result?.data?.success) {
        toast.success("Referral code permanently deleted");
        fetchReferral();
      } else {
        toast.error(result?.data?.message || "Failed to delete referral");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to delete referral");
    } finally {
      closeDeleteModal();
    }
  };

  const columns = [
    { name: "Name", selector: (row) => row.name },
    { name: "Email", selector: (row) => row.email },
    { name: "Referral Code", selector: (row) => row.code },
    { name: "Number of Uses", selector: (row) => row.clicks },
    // { name: "Sales", selector: (row) => row.total_sales_count },
    { name: "Revenue", selector: (row) => row.revenue },
    { name: "Discount (%)", selector: (row) => row.discount },
    { name: "Commission (%)", selector: (row) => row.commission },
    {name:"Commission",selector:(row)=>row.commission_revenue},
    {
      name: "Action",
      cell: (row) => (
        <div style={{ display: 'flex', gap: '8px' }}>
          <Badge
            badgeClassOverride={styles.badgeClassOverride}
            label={row?.is_active ? "Deactivate" : "Activate"}
            style={{ backgroundColor: row?.is_active ? "#E34A4A" : "Green", color: "white" }}
            onClick={() => deleteSale(row)}
          />
          <Badge
            badgeClassOverride={styles.badgeClassOverride}
            label="Delete"
            style={{ backgroundColor: "#333", color: "white" }}
            onClick={() => openDeleteModal(row)}
          />
        </div>
      ),
      width: "200px",
    },
  ];

  const [data, setData] = useState([]);

  const customStyles = {
    headCells: {
      style: { backgroundColor: "#F7F7F7" },
    },
  };

  const fetchReferral = async (e) => {
    try {
      e?.preventDefault();

      const result = await getData(`referrals/event-referrals`,false);
      if (result?.data?.data) {
        setData(result?.data?.data);
      }
    } catch (error) {}
  };

  const fetchReferral_afterPost = async (e) => {
    try {
      e?.preventDefault();

      const result = await getData(`referrals/event-referrals`);
      if (result?.data?.data) {
        setData(result?.data?.data);
      }
    } catch (error) {}
  };

  useEffect(() => {
    fetchReferral();
  }, []);

  // Filter data based on selected event
  const filteredData = eventFilter
    ? data.filter(item => item.event_id === eventFilter.value)
    : data;

  const handleReferral = async (e) => {
    try {
      e?.preventDefault();

      // Validate the code before submitting
      if (!referrals?.code) {
        toast.error("Please enter a discount code");
        return;
      }

      if (!isValidCode(referrals.code)) {
        toast.error("Code must be 3-20 alphanumeric characters (no spaces or special characters)");
        return;
      }

      const result = await postData("referrals/event-referrals", {
        ...referrals,
        event_id: referrals?.event_name?.value,
        providedCode: referrals.code.toUpperCase(),
      }, false);
      console.log("result is", result);
      if(result?.status == 201){
        toast.success("Discount code created successfully!");
        setReferrals(defaultValues);
        fetchReferral_afterPost();
      }
    } catch (error) {
      if (error?.response?.status === 409) {
        toast.error("This discount code already exists. Please use a different code.");
      } else {
        toast.error(error?.response?.data?.message || "Failed to create discount code");
      }
    }
  };

  return (
    <>
      <ContainerHeader label="Create Referral/Discount" showAddButton={false} />

      <form className={styles.form} onSubmit={handleReferral}>
        <FormFields Fields={formFields} />
        <Button label={"Create Code"} type="submit" />
      </form>

      {/* Event Filter - Same style as Orders page */}
      <div className={styles.dropdownWrapper}>
        <SelectSearch
          id="event-filter"
          placeholder="Filter by Event"
          value={eventFilter}
          onChange={(val) => setEventFilter(val)}
          loadOptions={fetchOptions}
          formGroupStyleOverride={{ width: "30rem", marginBottom: "0" }}
        />
      </div>

      <DataTable_
        columns={columns}
        data={[...filteredData]}
        customStyles={customStyles}
        conditionalRowStyles={[
          {
            when: (row) => row.id === "total",
            style: { backgroundColor: "#f0f0f0" },
          },
        ]}
        pagination
      />

      {/* Delete Confirmation Modal */}
      <PopUpModal modalIsOpen={deleteModalOpen} setModalIsOpen={setDeleteModalOpen}>
        <div style={{ textAlign: 'center' }}>
          <h3 style={{ marginBottom: '1rem', color: '#333' }}>Delete Referral Code</h3>
          <p style={{ marginBottom: '1.5rem', color: '#666' }}>
            Are you sure you want to permanently delete the referral code <strong>"{rowToDelete?.code}"</strong>?
            <br /><br />
            This action cannot be undone.
          </p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
            <Button 
              label="Cancel" 
              onClick={closeDeleteModal}
              style={{ backgroundColor: '#888', color: 'white' }}
            />
            <Button 
              label="Delete" 
              onClick={permanentDelete}
              style={{ backgroundColor: '#E34A4A', color: 'white' }}
            />
          </div>
        </div>
      </PopUpModal>
    </>
  );
};

export default Referral;
